import { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import consoleUserService from "../../services/consoleUserService";

function CreateNewUserModal({ visible, hideCreateNewUserModal, user = null }) {
  const editing = Boolean(user?._id);
  const [show, setShow] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [userType, setUserType] = useState("consoleAuditor");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setShow(visible);
    if (visible) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setUserType(user?.userType || "consoleAuditor");
      setPassword("");
      setConfirmPassword("");
    }
  }, [visible, user]);

  const handleClose = () => {
    setShow(false);
    hideCreateNewUserModal(null);
  };

  const passwordOk = editing
    ? (!password && !confirmPassword) || (password && password === confirmPassword && password.length >= 4)
    : Boolean(password && password === confirmPassword && password.length >= 4);

  const canSubmit = Boolean(name.trim() && email.trim() && passwordOk && !busy);

  async function saveUser() {
    if (!canSubmit) return;
    if (password && password !== confirmPassword) {
      toast.error("Passwörter stimmen nicht überein.");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const payload = {
          name: name.trim(),
          email: email.trim(),
          userType,
        };
        if (password) payload.password = password;
        const data = await consoleUserService.updateConsoleUser(user._id, payload);
        toast.success(data.message || "Benutzer aktualisiert.");
        hideCreateNewUserModal(data.user || { _id: user._id, name, email, userType });
      } else {
        const data = await consoleUserService.createConsoleUser({
          name: name.trim(),
          email: email.trim(),
          password,
          userType,
        });
        toast.success(data.message || "Benutzer angelegt.");
        hideCreateNewUserModal(data.user || { name, email, userType });
      }
      setShow(false);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || "Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{editing ? "Console-Benutzer bearbeiten" : "Neuen Console-Benutzer anlegen"}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form autoComplete="off" onSubmit={(e) => e.preventDefault()}>
          <div className="mb-3">
            <label htmlFor="consoleUserName" className="form-label">Name</label>
            <input
              type="text"
              className="form-control"
              id="consoleUserName"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="consoleUserEmail" className="form-label">E-Mail</label>
            <input
              type="email"
              className="form-control"
              id="consoleUserEmail"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="consoleUserPassword" className="form-label">
              Passwort{editing ? " (leer lassen = unverändert)" : ""}
            </label>
            <input
              type="password"
              className="form-control"
              id="consoleUserPassword"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
              autoComplete="new-password"
            />
          </div>
          <div className="mb-3">
            <label htmlFor="consoleUserConfirmPassword" className="form-label">Passwort bestätigen</label>
            <input
              type="password"
              className="form-control"
              id="consoleUserConfirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              disabled={busy}
              autoComplete="new-password"
            />
            {confirmPassword && password && password !== confirmPassword ? (
              <div className="form-text text-danger">Passwörter stimmen nicht überein.</div>
            ) : null}
          </div>

          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="consoleUserType"
              id="radioAuditor"
              checked={userType === "consoleAuditor"}
              onChange={() => setUserType("consoleAuditor")}
              disabled={busy}
            />
            <label className="form-check-label" htmlFor="radioAuditor">Auditor</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="consoleUserType"
              id="radioTeacher"
              checked={userType === "consoleTeacher"}
              onChange={() => setUserType("consoleTeacher")}
              disabled={busy}
            />
            <label className="form-check-label" htmlFor="radioTeacher">Lehrer</label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="radio"
              name="consoleUserType"
              id="radioAdministrator"
              checked={userType === "consoleAdministrator"}
              onChange={() => setUserType("consoleAdministrator")}
              disabled={busy}
            />
            <label className="form-check-label" htmlFor="radioAdministrator">Administrator</label>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={busy}>Abbrechen</Button>
        {canSubmit ? (
          <button type="button" className="btn btn-success" onClick={saveUser} disabled={busy}>
            {busy ? "…" : editing ? "Speichern" : "Anlegen"}
          </button>
        ) : null}
      </Modal.Footer>
    </Modal>
  );
}

export default CreateNewUserModal;
